import { Section } from "design-system/molecules/Section"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema, CampaignTriggerTypeEnum } from "schema"
import { z } from "zod"

import { InstagramCommentReplyForm } from "./forms/InstagramCommentReplyForm"
import { InstagramDirectMessageForm } from "./forms/InstagramDirectMessageForm"
import { LinkMetadataInfo } from "./forms/LinkMetadataInfo"
import { QRMetadataForm } from "./forms/QRMetadataForm"
import { ShopifyTriggerForm } from "./forms/ShopifyTriggerForm"
import { BigBlueTriggerForm } from "./forms/BigBlueTriggerForm"
import { WebhookMetadataInfo } from "./forms/WebhookMetadataInfo"
import { WidgetMetadataInfo } from "./forms/WidgetMetadataInfo"

type Props = {
  isEditable: boolean
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

export function TriggerConfigSubTab({ isEditable, campaign }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const trigger = form.watch("trigger")

  if (trigger === CampaignTriggerTypeEnum.QR) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.qr.title")}
        description={t("campaigns.configuration.setup.config.qr.description")}
        className="gap-3"
      >
        <QRMetadataForm isDisabled={!isEditable} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.InstagramDirectMessage) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.instagramDirectMessage.title")}
        description={t("campaigns.configuration.setup.config.instagramDirectMessage.description")}
        className="gap-3"
      >
        <InstagramDirectMessageForm isDisabled={!isEditable} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.InstagramCommentReply) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.instagramCommentReply.title")}
        description={t("campaigns.configuration.setup.config.instagramCommentReply.description")}
        className="gap-3"
      >
        <InstagramCommentReplyForm isDisabled={!isEditable} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.Link) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.link.title")}
        description={t("campaigns.configuration.setup.config.link.description")}
        className="gap-3"
      >
        <LinkMetadataInfo campaign={campaign} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.Widget) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.widget.title")}
        description={t("campaigns.configuration.setup.config.widget.description")}
        className="gap-3"
      >
        <WidgetMetadataInfo campaign={campaign} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.Webhook) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.webhook.title")}
        description={t("campaigns.configuration.setup.config.webhook.description")}
        className="gap-3"
      >
        <WebhookMetadataInfo campaign={campaign} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.Shopify) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.shopify.title")}
        description={t("campaigns.configuration.setup.config.shopify.description")}
        className="gap-3"
      >
        <ShopifyTriggerForm isDisabled={!isEditable} />
      </Section>
    )
  }

  if (trigger === CampaignTriggerTypeEnum.BigBlue) {
    return (
      <Section
        title={t("campaigns.configuration.setup.config.bigblue.title")}
        description={t("campaigns.configuration.setup.config.bigblue.description")}
        className="gap-3"
      >
        <BigBlueTriggerForm isDisabled={!isEditable} />
      </Section>
    )
  }

  return null
}
